import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Image, Badge,
  Spinner, Alert, Carousel, Modal, Button
} from 'react-bootstrap';
import axios from 'axios';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const OrganizationPublic = () => {
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState('');

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch active organization(s)
      const response = await axios.get(`${API_BASE_URL}/organizations.php?is_active=1`);
      console.log('Organization data:', response.data);
      
      if (response.data.success && response.data.data.length > 0) {
        // Get the first active organization
        // You can modify this logic if you need to handle multiple organizations
        setOrganization(response.data.data[0]);
      } else {
        setError('No active organization found');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error loading organization details');
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLogoClick = (logoPath) => {
    setSelectedLogo(logoPath);
    setShowLogoModal(true);
  };

  if (loading) {
    return (
      <Container className="text-center my-5 py-5">
        <Spinner animation="border" variant="primary" />
        <h4 className="mt-3">Loading Organization Details...</h4>
        <p className="text-muted">Please wait while we load the information</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <Alert variant="warning">
          <Alert.Heading>
            <i className="fas fa-exclamation-triangle me-2"></i>
            Organization Information Not Available
          </Alert.Heading>
          <p>{error}</p>
          <div className="d-flex justify-content-center mt-3">
            <div className="text-center">
              <i className="fas fa-building fa-4x text-muted mb-3"></i>
              <p className="text-muted">Organization details will be available soon</p>
            </div>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Hero Section */}
      <div className="bg-primary text-white py-5 mb-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={2} className="text-center mb-4 mb-lg-0">
              {organization.logo_path && (
                <div 
                  className="logo-container"
                  style={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  onClick={() => handleLogoClick(organization.logo_path)}
                >
                  <Image
                    src={getImageUrl(organization.logo_path)}
                    alt={organization.logo_alt_text || organization.organization_name}
                    fluid
                    className="shadow-lg rounded-circle border border-4 border-white"
                    style={{ 
                      width: '150px', 
                      height: '150px', 
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      padding: '5px'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://via.placeholder.com/150x150?text=${organization.short_name || organization.organization_name.charAt(0)}`;
                      e.target.className = 'shadow-lg rounded-circle border border-4 border-white';
                    }}
                  />
                </div>
              )}
            </Col>
            <Col lg={10}>
              <div className="text-center text-lg-start">
                <h1 className="display-4 fw-bold mb-2">
                  {organization.organization_name}
                  {organization.short_name && (
                    <Badge bg="light" text="primary" className="ms-3 fs-5 align-middle">
                      {organization.short_name}
                    </Badge>
                  )}
                </h1>
                {organization.tagline && (
                  <p className="lead mb-4 fst-italic">{organization.tagline}</p>
                )}
                <div className="d-flex flex-wrap justify-content-center justify-content-lg-start gap-2 mt-4">
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Active Organization
                  </Badge>
                  <Badge bg="light" text="dark" className="px-3 py-2">
                    <i className="fas fa-calendar-alt text-primary me-2"></i>
                    Since {formatDate(organization.created_at)}
                  </Badge>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="mb-5">
        <Row>
          {/* Description Section */}
          <Col lg={8}>
            <Card className="shadow-sm mb-4 border-0">
              <Card.Body className="p-4">
                <Card.Title className="mb-4">
                  <h3 className="text-primary">
                    <i className="fas fa-info-circle me-2"></i>
                    About Our Organization
                  </h3>
                </Card.Title>
                {organization.description ? (
                  <div className="description-content">
                    {organization.description.split('\n').map((paragraph, index) => (
                      <p key={index} className="fs-5 text-muted mb-3">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                    <p className="text-muted">Description not available</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Details Card */}
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <h4 className="mb-4 text-primary">
                  <i className="fas fa-clipboard-list me-2"></i>
                  Organization Details
                </h4>
                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted small mb-2">
                        <i className="fas fa-signature me-2"></i>
                        Full Name
                      </h6>
                      <p className="fs-5">{organization.organization_name}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted small mb-2">
                        <i className="fas fa-hashtag me-2"></i>
                        Short Name
                      </h6>
                      <p className="fs-5">
                        {organization.short_name ? (
                          <Badge bg="primary" className="fs-5 px-3 py-2">
                            {organization.short_name}
                          </Badge>
                        ) : (
                          <span className="text-muted">Not specified</span>
                        )}
                      </p>
                    </div>
                  </Col>
                  
                  <Col md={6}>
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted small mb-2">
                        <i className="fas fa-calendar-plus me-2"></i>
                        Created On
                      </h6>
                      <p className="fs-5">{formatDate(organization.created_at)}</p>
                    </div>
                    
                    <div className="mb-4">
                      <h6 className="text-uppercase text-muted small mb-2">
                        <i className="fas fa-sync-alt me-2"></i>
                        Last Updated
                      </h6>
                      <p className="fs-5">{formatDate(organization.updated_at)}</p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          {/* Sidebar */}
          <Col lg={4}>
            {/* Logo Preview Card */}
            <Card className="shadow-sm mb-4 border-0">
              <Card.Body className="text-center p-4">
                <Card.Title className="mb-3">
                  <h4 className="text-primary">
                    <i className="fas fa-image me-2"></i>
                    Organization Logo
                  </h4>
                </Card.Title>
                
                {organization.logo_path ? (
                  <>
                    <div 
                      className="logo-preview mb-3"
                      style={{ 
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onClick={() => handleLogoClick(organization.logo_path)}
                    >
                      <Image
                        src={getImageUrl(organization.logo_path)}
                        alt={organization.logo_alt_text || organization.organization_name}
                        fluid
                        className="rounded shadow"
                        style={{ 
                          maxHeight: '200px',
                          objectFit: 'contain',
                          backgroundColor: 'white',
                          padding: '10px'
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://via.placeholder.com/300x200?text=${organization.short_name || 'Logo'}`;
                        }}
                      />
                    </div>
                    <p className="text-muted small">
                      <i className="fas fa-mouse-pointer me-1"></i>
                      Click to view larger
                    </p>
                    {organization.logo_alt_text && organization.logo_alt_text !== 'Organization Logo' && (
                      <div className="mt-3">
                        <p className="text-muted mb-1">
                          <i className="fas fa-info-circle me-2"></i>
                          Image Description:
                        </p>
                        <p className="small">{organization.logo_alt_text}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="py-4">
                    <div className="d-flex justify-content-center align-items-center bg-light rounded"
                         style={{ width: '100%', height: '150px' }}>
                      <i className="fas fa-building fa-3x text-muted"></i>
                    </div>
                    <p className="text-muted mt-3">No logo available</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Tagline Card */}
            {organization.tagline && (
              <Card className="shadow-sm mb-4 border-0 bg-gradient-primary text-white">
                <Card.Body className="p-4 text-center">
                  <i className="fas fa-quote-left fa-2x mb-3 opacity-75"></i>
                  <h3 className="fst-italic mb-3">"{organization.tagline}"</h3>
                  <i className="fas fa-quote-right fa-2x mt-3 opacity-75"></i>
                  <div className="mt-3">
                    <i className="fas fa-bullhorn me-2"></i>
                    Our Tagline
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Status Card */}
            <Card className="shadow-sm border-0">
              <Card.Body className="p-4">
                <h5 className="mb-3 text-primary">
                  <i className="fas fa-chart-line me-2"></i>
                  Organization Status
                </h5>
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="d-flex align-items-center mb-2">
                      <div className={`rounded-circle p-2 me-3 ${organization.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        <i className={`fas fa-${organization.is_active ? 'check' : 'pause'} text-white`}></i>
                      </div>
                      <div>
                        <h5 className="mb-0">
                          {organization.is_active ? 'Active' : 'Inactive'}
                        </h5>
                        <p className="text-muted small mb-0">
                          {organization.is_active ? 'Currently operational' : 'Currently not active'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4">
                  <div className="row text-center">
                    <div className="col-6">
                      <div className="border-end">
                        <h4 className="text-primary mb-1">
                          <i className="fas fa-calendar-plus"></i>
                        </h4>
                        <small className="text-muted">Created</small>
                        <p className="mb-0 fw-bold">
                          {new Date(organization.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="col-6">
                      <h4 className="text-primary mb-1">
                        <i className="fas fa-history"></i>
                      </h4>
                      <small className="text-muted">Updated</small>
                      <p className="mb-0 fw-bold">
                        {new Date(organization.updated_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Contact/CTA Card */}
            <Card className="shadow-sm border-0 mt-4 bg-light">
              <Card.Body className="p-4 text-center">
                <h5 className="mb-3">
                  <i className="fas fa-handshake me-2 text-primary"></i>
                  Get in Touch
                </h5>
                <p className="text-muted mb-4">
                  Interested in learning more about our organization?
                </p>
                <Button variant="outline-primary" size="lg" className="w-100">
                  <i className="fas fa-envelope me-2"></i>
                  Contact Us
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Additional Info Section - Only shows if there's data */}
        {(organization.description && organization.description.length > 500) && (
          <Row className="mt-5">
            <Col>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-5">
                  <h3 className="text-center text-primary mb-4">
                    <i className="fas fa-book-open me-2"></i>
                    More About Us
                  </h3>
                  <div className="row">
                    <div className="col-md-4 text-center mb-4 mb-md-0">
                      <div className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center"
                           style={{ width: '80px', height: '80px' }}>
                        <i className="fas fa-bullseye fa-2x"></i>
                      </div>
                      <h5 className="mt-3 mb-2">Our Mission</h5>
                      <p className="text-muted">
                        Dedicated to excellence and innovation in our field
                      </p>
                    </div>
                    <div className="col-md-4 text-center mb-4 mb-md-0">
                      <div className="rounded-circle bg-success text-white d-inline-flex align-items-center justify-content-center"
                           style={{ width: '80px', height: '80px' }}>
                        <i className="fas fa-eye fa-2x"></i>
                      </div>
                      <h5 className="mt-3 mb-2">Our Vision</h5>
                      <p className="text-muted">
                        Creating a better future through our work
                      </p>
                    </div>
                    <div className="col-md-4 text-center">
                      <div className="rounded-circle bg-info text-white d-inline-flex align-items-center justify-content-center"
                           style={{ width: '80px', height: '80px' }}>
                        <i className="fas fa-heart fa-2x"></i>
                      </div>
                      <h5 className="mt-3 mb-2">Our Values</h5>
                      <p className="text-muted">
                        Integrity, Excellence, and Community
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>

      {/* Footer Section */}
      <footer className="bg-dark text-white py-5 mt-5">
        <Container>
          <Row>
            <Col lg={4} className="mb-4 mb-lg-0">
              <h5 className="mb-3">
                {organization.short_name || organization.organization_name}
              </h5>
              <p className="text-light small">
                {organization.tagline || "Excellence in everything we do"}
              </p>
              {organization.logo_path && (
                <Image
                  src={getImageUrl(organization.logo_path)}
                  alt={organization.logo_alt_text || organization.organization_name}
                  style={{ width: '80px', height: '80px', objectFit: 'contain' }}
                  className="bg-white rounded p-2"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://via.placeholder.com/80x80?text=${organization.short_name || 'Logo'}`;
                  }}
                />
              )}
            </Col>
            <Col lg={4} className="mb-4 mb-lg-0">
              <h5 className="mb-3">Quick Links</h5>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <a href="#about" className="text-light text-decoration-none">
                    <i className="fas fa-chevron-right me-2"></i>
                    About Us
                  </a>
                </li>
                <li className="mb-2">
                  <a href="#services" className="text-light text-decoration-none">
                    <i className="fas fa-chevron-right me-2"></i>
                    Our Services
                  </a>
                </li>
                <li className="mb-2">
                  <a href="#contact" className="text-light text-decoration-none">
                    <i className="fas fa-chevron-right me-2"></i>
                    Contact Us
                  </a>
                </li>
              </ul>
            </Col>
            <Col lg={4}>
              <h5 className="mb-3">Contact Info</h5>
              <ul className="list-unstyled text-light small">
                <li className="mb-2">
                  <i className="fas fa-building me-2"></i>
                  {organization.organization_name}
                </li>
                <li className="mb-2">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Established: {formatDate(organization.created_at)}
                </li>
                <li className="mb-2">
                  <i className="fas fa-check-circle me-2 text-success"></i>
                  Status: {organization.is_active ? 'Active' : 'Inactive'}
                </li>
              </ul>
            </Col>
          </Row>
          <hr className="bg-light mt-4" />
          <Row className="mt-4">
            <Col className="text-center">
              <p className="mb-0 text-light small">
                &copy; {new Date().getFullYear()} {organization.organization_name}. All rights reserved.
              </p>
              <p className="text-light small mt-2">
                Last updated: {formatDate(organization.updated_at)}
              </p>
            </Col>
          </Row>
        </Container>
      </footer>

      {/* Logo Modal */}
      <Modal show={showLogoModal} onHide={() => setShowLogoModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-image me-2"></i>
            {organization?.organization_name} Logo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedLogo && (
            <Image
              src={getImageUrl(selectedLogo)}
              alt={organization?.logo_alt_text || organization?.organization_name}
              fluid
              style={{ maxHeight: '70vh', objectFit: 'contain' }}
              className="bg-light rounded p-3"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://via.placeholder.com/800x600?text=${organization?.short_name || 'Logo'}`;
              }}
            />
          )}
          {organization?.logo_alt_text && organization.logo_alt_text !== 'Organization Logo' && (
            <div className="mt-3">
              <p className="text-muted">
                <i className="fas fa-info-circle me-2"></i>
                {organization.logo_alt_text}
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLogoModal(false)}>
            Close
          </Button>
          <Button 
            variant="primary"
            onClick={() => {
              const link = document.createElement('a');
              link.href = getImageUrl(selectedLogo);
              link.download = `${organization?.organization_name}_logo`;
              link.click();
            }}
          >
            <i className="fas fa-download me-2"></i>
            Download Logo
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrganizationPublic;